import React from 'react';
import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useFetcher, Form } from 'react-router-dom';

import { Button } from '@oysterjs/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { AutoResizeTextarea, Textarea } from '@oysterjs/ui/textarea';
import { ActionResult } from '@oysterjs/core/graphql/actions';

import { FileInput } from '~/components/file';
import { useDefaultVisibilityValue } from '~/components/visibility/default-visibility';
import { useVisibilityEntitlement } from '~/components/visibility/use-visibility-entitlement';
import { VisibilitySplitButton } from '~/components/visibility/visibility-popover';
import {
  visibilityInputFromValue,
  visibilityValueFromInput,
  type VisibilitySelectorValue
} from '~/components/visibility/visibility-selector.utils';

import { ValidationError } from '@oysterjs/core/graphql';
import { useFormActions } from '@oysterjs/core/graphql/form';

import { SectionedTemplateFields } from './word-template-section-fields';
import {
  appendSectionsToFormData,
  createDefaultSingleSection,
  sectionsWithSimpleModeTitleFallback,
  type WordTemplateFormState
} from '~/components/library/lib/word-template-sections';

const WORD_DEFAULT_DESCRIPTION =
  'Word document template for Cara to generate documents from your agency layout.';

const WORD_EXTENSION_RE = /\.docx$/i;

export type WordTemplateCreateDialogMode = 'upload' | 'duplicate';

export interface WordTemplateCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** POST action URL, e.g. `/app/library/word-templates/new`. The action is
   *  expected to perform the upload and revalidate the parent route. */
  action: string;
  /** When set, opening the dialog seeds the form from a template copy (reuses file IDs). */
  initialForm?: WordTemplateFormState | null;
  initialSectioned?: boolean;
  mode?: WordTemplateCreateDialogMode;
  /** Called after a successful create, before `onClose`. */
  onSuccess?: () => void;
}

/**
 * Upload a .docx Word template to the library. Submits through the router
 * fetcher so the parent loader revalidates after upload; the dialog shows
 * an animated progress overlay while the action runs and closes itself once
 * the action returns idle without a payload error.
 *
 * Custom-form upload uses its own dialog (Apollo path, no progress overlay,
 * no extension validation).
 */
const defaultEmptyForm = (visibility: VisibilitySelectorValue): WordTemplateFormState => ({
  title: '',
  description: '',
  fillGuidance: '',
  sections: [createDefaultSingleSection()],
  visibility
});

export function WordTemplateCreateDialog({
  open,
  onClose,
  action,
  initialForm = null,
  initialSectioned,
  mode = 'upload',
  onSuccess
}: WordTemplateCreateDialogProps) {
  const fetcher = useFetcher();

  const visibilityEnabled = useVisibilityEntitlement();
  const defaultVisibility = useDefaultVisibilityValue();
  const [visibility, setVisibility] = React.useState<VisibilitySelectorValue>(defaultVisibility);

  const [sectioned, setSectioned] = React.useState(false);
  const [form, setForm] = React.useState<WordTemplateFormState>({
    title: '',
    description: '',
    fillGuidance: '',
    sections: [createDefaultSingleSection()],
    visibility: defaultVisibility
  });
  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const { updateForm, getValidationError } = useFormActions<WordTemplateFormState>(
    setForm,
    setValidationErrors
  );
  const [error, setError] = React.useState<string>();

  /** True between handleSubmit and the next idle that follows it. Used to
   *  distinguish "we just submitted" from "idle since mount" so we don't
   *  auto-close the dialog every time the parent route revalidates. */
  const pendingSubmitRef = React.useRef(false);
  /** Bumped on each submission so the LibraryFileUploadProgress remounts
   *  with a fresh animation timeline (otherwise a retry would resume from
   *  the previous run's ~88% cap). */
  const uploadAttemptIdRef = React.useRef(0);

  // Remix: the action runs during 'submitting'; revalidation/navigation uses
  // 'loading'. Both count as in-flight for the purposes of locking the UI.
  const fetcherInFlight = fetcher.state === 'submitting' || fetcher.state === 'loading';

  const routeActionPayload =
    fetcher.data && typeof fetcher.data === 'object'
      ? (fetcher.data as NonNullable<ActionResult<unknown>>)
      : null;
  const routeActionFailed =
    !!routeActionPayload &&
    (Boolean(routeActionPayload.applicationError) ||
      (routeActionPayload.validationErrors?.length ?? 0) > 0);

  React.useEffect(() => {
    pendingSubmitRef.current = false;
    if (!open) {
      return;
    }
    setError(undefined);
    setValidationErrors([]);
    if (initialForm) {
      setForm(initialForm);
      setSectioned(initialSectioned ?? initialForm.sections.length > 1);
      setVisibility(
        initialForm.visibility
          ? visibilityValueFromInput(initialForm.visibility)
          : defaultVisibility
      );
    } else {
      setSectioned(false);
      setForm(defaultEmptyForm(defaultVisibility));
      setVisibility(defaultVisibility);
    }
  }, [open, initialForm, initialSectioned, defaultVisibility]);

  // After a submission we wait for the fetcher to return to idle (action +
  // revalidation finished) before either closing the dialog (success) or
  // surfacing the error (failure). In both branches we clear pendingSubmitRef
  // so the next idle render does not re-trigger this effect.
  React.useEffect(() => {
    if (fetcher.state !== 'idle' || !pendingSubmitRef.current) return;
    pendingSubmitRef.current = false;
    if (routeActionFailed) {
      if (routeActionPayload?.applicationError) {
        setError(routeActionPayload.applicationError.message ?? 'Upload failed');
        return;
      }
      if (routeActionPayload?.validationErrors?.length) {
        setValidationErrors(routeActionPayload.validationErrors);
        return;
      }
    }
    onSuccess?.();
    onClose();
  }, [defaultVisibility, fetcher.state, onClose, onSuccess, routeActionFailed, routeActionPayload]);

  const handleClose = () => {
    if (fetcherInFlight) return;
    onClose();
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // In simple mode, blank titles are normalized to the simple-mode default
    // here so local state can keep them empty (lets the user fill in their
    // own title if they toggle to sectioned mode). In sectioned mode we send
    // titles through as-is — the server enforces the required-title rule
    // and reports it back as a `sections.<i>.title` validation error.
    const sectionsToSubmit = sectioned
      ? form.sections
      : sectionsWithSimpleModeTitleFallback(form.sections);

    // Client-side guards: reject any non-.docx files up front, and block
    // submission of a sectioned template with no sections (the backend would
    // return a `uploadTemplate` error that has no UI to render against in
    // sectioned mode). Other rules (missing file per section, missing title
    // per section, etc.) are enforced server-side and surface via
    // validationErrors so per-section errors render in the right card.
    if (sectioned && sectionsToSubmit.length === 0) {
      setError('Add at least one section before uploading.');
      return;
    }

    const badExtension = sectionsToSubmit
      .map((s) => s.uploadTemplate)
      .find((f): f is File => !!f && !WORD_EXTENSION_RE.test(f.name));
    if (badExtension) {
      setError('Upload a .docx file only, not .doc, .pdf, .pages, .dotx, or other formats.');
      return;
    }
    setError(undefined);

    const formData = new FormData();
    formData.set('title', form.title);
    formData.set('description', form.description);
    formData.set('fillGuidance', form.fillGuidance ?? '');
    if (visibilityEnabled) {
      formData.set('visibility', JSON.stringify(visibilityInputFromValue(visibility)));
    }

    appendSectionsToFormData(formData, sectionsToSubmit);

    pendingSubmitRef.current = true;
    uploadAttemptIdRef.current += 1;
    fetcher.submit(formData, {
      method: 'POST',
      action,
      encType: 'multipart/form-data'
    });
  };

  const isDuplicate = mode === 'duplicate';

  return (
    <Dialog open={open} onClose={handleClose} size="2xl">
      <DialogTitle>{isDuplicate ? 'Duplicate Word template' : 'Upload Word template'}</DialogTitle>
      <DialogDescription>
        {isDuplicate
          ? 'Review and edit this copy before creating a new library entry. Existing .docx files are reused.'
          : 'Add a .docx file to your library so Cara can fill it and produce new Word documents.'}
      </DialogDescription>
      <Form onSubmit={handleSubmit}>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              {!isDuplicate && (
                <Field>
                  <Label>Template type</Label>
                  <Description>Choose the type of template you want to set up.</Description>
                  <Headless.RadioGroup
                    data-slot="control"
                    value={sectioned ? 'sectioned' : 'simple'}
                    onChange={(value) => {
                      const nextSectioned = value === 'sectioned';
                      setSectioned(nextSectioned);
                      if (!nextSectioned) {
                        // Collapse back to the single default section, carrying
                        // over the first available file so the user doesn't
                        // lose what they uploaded.
                        updateForm(['sections'], (prev) => {
                          const carriedFile =
                            prev.sections.find((s) => !!s.uploadTemplate)?.uploadTemplate ??
                            prev.uploadTemplate ??
                            null;
                          return {
                            ...prev,
                            uploadTemplate: carriedFile,
                            sections: [createDefaultSingleSection(carriedFile)]
                          };
                        });
                      }
                    }}
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <Headless.Radio
                        value="simple"
                        aria-label="Simple"
                        aria-description="A simple template with a single section. Less than 6 pages long."
                        className={clsx([
                          'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[disabled]:pointer-events-none',
                          'group relative flex cursor-pointer rounded-lg bg-white p-4 shadow-sm focus:outline-none border border-neutral-950/10 data-[hover]:border-neutral-950/20  data-[focus]:border-primary-500 data-[focus]:ring-2 data-[focus]:ring-primary-500 dark:bg-neutral-800 dark:border-white/10 dark:data-[hover]:border-white/20'
                        ])}
                      >
                        <span className="flex gap-1.5 flex-1">
                          <span className="flex flex-col">
                            <span className="block text-base sm:text-sm font-medium text-neutral-900 dark:text-white">
                              Simple
                            </span>
                            <span className="mt-1 flex items-center text-sm sm:text-xs text-neutral-500 dark:text-neutral-400 group-data-[disabled]:text-neutral-600 dark:group-data-[disabled]:text-neutral-300">
                              A simple template with a single section, with a total length of less
                              than 6 pages.
                            </span>
                          </span>
                        </span>
                        <CheckCircleIcon
                          aria-hidden="true"
                          className="h-5 w-5 text-primary-500 dark:text-primary-400 [.group:not([data-checked])_&]:invisible"
                        />
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute -inset-px rounded-lg border-2 border-transparent group-data-[focus]:border group-data-[checked]:border-primary-500"
                        />
                      </Headless.Radio>
                      <Headless.Radio
                        value="sectioned"
                        aria-label="Sectioned"
                        aria-description="An account for an individual."
                        className={clsx([
                          'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[disabled]:pointer-events-none',
                          'group relative flex cursor-pointer rounded-lg bg-white p-4 shadow-sm focus:outline-none border border-neutral-950/10 data-[hover]:border-neutral-950/20  data-[focus]:border-primary-500 data-[focus]:ring-2 data-[focus]:ring-primary-500 dark:bg-neutral-800 dark:border-white/10 dark:data-[hover]:border-white/20'
                        ])}
                      >
                        <span className="flex gap-1.5 flex-1">
                          <span className="flex flex-col">
                            <span className="block sm:text-sm font-medium text-neutral-900 dark:text-white">
                              Sectioned
                            </span>
                            <span className="mt-1 flex items-center text-sm sm:text-xs text-neutral-500 dark:text-neutral-400 group-data-[disabled]:text-neutral-600 dark:group-data-[disabled]:text-neutral-300">
                              A sectioned template, some of which are static, repeatable, or
                              conditional.
                            </span>
                          </span>
                        </span>
                        <CheckCircleIcon
                          aria-hidden="true"
                          className="h-5 w-5 text-primary-500 dark:text-primary-400 [.group:not([data-checked])_&]:invisible"
                        />
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute -inset-px rounded-lg border-2 border-transparent group-data-[focus]:border group-data-[checked]:border-primary-500"
                        />
                      </Headless.Radio>
                    </div>
                  </Headless.RadioGroup>
                </Field>
              )}

              <Field>
                <Label>Template title</Label>
                <Description>
                  Shown in your Library list. Defaults from the file name when you pick a file.
                </Description>
                <Input
                  invalid={!!getValidationError(validationErrors, ['title'], { exact: true })}
                  value={form.title}
                  onChange={(e) => {
                    const v = e.currentTarget.value;
                    updateForm(['title'], (prev) => ({ ...prev, title: v }));
                  }}
                  placeholder="e.g. Agency proposal letter"
                />
                {getValidationError(validationErrors, ['title'], { exact: true }) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['title'], { exact: true })}
                  </ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Description</Label>
                <Description>Helps your team recognize this template in the Library.</Description>
                <Textarea
                  invalid={!!getValidationError(validationErrors, ['description'])}
                  value={form.description}
                  onChange={(e) => {
                    const v = e.currentTarget.value;
                    updateForm(['description'], (prev) => ({ ...prev, description: v }));
                  }}
                  rows={3}
                  placeholder={WORD_DEFAULT_DESCRIPTION}
                />
                {getValidationError(validationErrors, ['description']) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['description'])}
                  </ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Setup guidance (optional)</Label>
                <Description>
                  Extra instructions for Cara when this template is listed in workspace tools, for
                  example how to use the document or which sections must stay verbatim.
                </Description>
                <AutoResizeTextarea
                  invalid={!!getValidationError(validationErrors, ['fillGuidance'])}
                  value={form.fillGuidance ?? ''}
                  onChange={(e) => {
                    const v = e.currentTarget.value;
                    updateForm(['fillGuidance'], (prev) => ({ ...prev, fillGuidance: v }));
                  }}
                  rows={4}
                  placeholder="Example: Ignore the table of contents."
                />
                {getValidationError(validationErrors, ['fillGuidance']) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['fillGuidance'])}
                  </ErrorMessage>
                )}
              </Field>

              {sectioned ? (
                <SectionedTemplateFields
                  form={form}
                  updateForm={updateForm}
                  getValidationError={(parts) => getValidationError(validationErrors, parts)}
                />
              ) : (
                <SimpleTemplateFields
                  form={form}
                  updateForm={updateForm}
                  getValidationError={(parts) => getValidationError(validationErrors, parts)}
                />
              )}

              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button type="button" color="white" onClick={handleClose} disabled={fetcherInFlight}>
            Cancel
          </Button>
          {visibilityEnabled ? (
            <VisibilitySplitButton
              type="submit"
              value={visibility}
              onChange={setVisibility}
              loading={fetcherInFlight}
            >
              {isDuplicate ? 'Create template' : 'Upload template'}
            </VisibilitySplitButton>
          ) : (
            <Button type="submit" color="sky" loading={fetcherInFlight}>
              {isDuplicate ? 'Create template' : 'Upload template'}
            </Button>
          )}
        </DialogActions>
      </Form>
    </Dialog>
  );
}

interface SimpleTemplateFieldsProps {
  form: WordTemplateFormState;
  updateForm: (
    path: string[],
    updater: (prev: WordTemplateFormState) => WordTemplateFormState
  ) => void;
  getValidationError: (path: string[]) => string | undefined;
}

const SimpleTemplateFields = (props: SimpleTemplateFieldsProps) => {
  // Simple mode is internally a single-section template, so server-side errors
  // on the file land at `sections.0.uploadTemplate`. We also check the legacy
  // `uploadTemplate` path so any errors raised against that field (older
  // backends or non-section flows) still display under this input.
  const fileError =
    props.getValidationError(['sections', '0', 'uploadTemplate']) ??
    props.getValidationError(['uploadTemplate']);
  return (
    <>
      <Field>
        <Label>Word file (.docx)</Label>
        <Description>
          .docx only. Not .doc, .pdf, .pages, or .dotx. One file per upload.
        </Description>
        <FileInput
          invalid={!!fileError}
          onChange={(change) => {
            const next = change.files[0]?.upload ?? null;
            // Simple mode is internally a single-section template with fixed
            // defaults. Mirror the picked file into both the legacy
            // `uploadTemplate` field (kept for backward compat with any
            // remaining legacy code paths) and the first slot of `sections`
            // so the form is consistent if/when the user toggles to
            // "sectioned" mode.
            props.updateForm(['uploadTemplate'], (prev) => ({
              ...prev,
              uploadTemplate: next,
              sections: [createDefaultSingleSection(next)]
            }));
          }}
        />
        {fileError && <ErrorMessage>{fileError}</ErrorMessage>}
      </Field>
    </>
  );
};
